<template>
  <HLButton
    :id="computedId"
    :variant="mappedVariant"
    :color="mappedColor"
    size="xs"
    :disabled="disabled"
    :loading="loading"
    :class="computedClasses"
    :aria-label="ariaLabel"
    @click="handleClick"
  >
    <template
      v-if="icon && iconPosition === 'left'"
      #iconLeft
    >
      <component
        :is="icon"
        aria-hidden="true"
      />
    </template>
    
    <slot />
    
    <template
      v-if="icon && iconPosition === 'right'"
      #iconRight
    >
      <component
        :is="icon"
        aria-hidden="true"
      />
    </template>
  </HLButton>
</template>

<script setup>
import { computed } from 'vue'
import { HLButton } from '@platform-ui/highrise'

// Props - maintain backward compatibility with existing Button API
const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => [
      'primary', 
      'secondary', 
      'outline',
      'danger', 
      'success', 
      'warning', 
      'ghost',
      'link'
    ].includes(value)
  },
  size: {
    type: String,
    default: 'xs',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl', '2xs', '2xl'].includes(value)
  },
  // Note: HLButton doesn't support type prop, but we accept it for compatibility
  type: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  fullWidth: {
    type: Boolean,
    default: false
  },
  icon: {
    type: [Object, Function],
    default: null
  },
  iconPosition: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'right'].includes(value)
  },
  // Custom color support
  color: {
    type: String,
    default: null
  },
  // Rounded prop for backward compatibility (HLButton uses default rounding)
  rounded: {
    type: String,
    default: 'md',
    validator: (value) => ['none', 'sm', 'md', 'lg', 'full'].includes(value)
  },
  // Accessibility
  ariaLabel: {
    type: String,
    default: null
  },
  // ID for the button
  id: {
    type: String,
    default: null
  }
})

// Emits
const emit = defineEmits(['click'])

// Generate a unique ID if not provided
let buttonIdCounter = 0
const computedId = computed(() => {
  if (props.id) return props.id
  return `button-${++buttonIdCounter}-${Date.now()}`
})

// Map old variant to HLButton variant
const mappedVariant = computed(() => {
  const variantMap = {
    'primary': 'primary',
    'secondary': 'secondary',
    'outline': 'secondary',  // HLButton's secondary is outlined
    'ghost': 'ghost',
    'link': 'text',
    'danger': 'secondary',   // Use with color='red'
    'success': 'secondary',  // Use with color='green'
    'warning': 'secondary'   // Use with color='orange'
  }
  
  return variantMap[props.variant] || 'secondary'
})

// Map color based on variant or explicit color prop
const mappedColor = computed(() => {
  // If explicit color is provided, use it
  if (props.color) return props.color
  
  // Map old semantic variants to appropriate colors
  const colorMap = {
    'danger': 'red',
    'success': 'green',
    'warning': 'orange',
    'primary': 'blue'
  }
  
  return colorMap[props.variant] || 'gray'
})

// Compute additional classes for compatibility
const computedClasses = computed(() => {
  const classes = []
  
  if (props.fullWidth) {
    classes.push('w-full')
  }
  
  return classes.join(' ')
})

// Handle click - emit click event
const handleClick = (event) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>
